import { get, has, pick, sortBy, isUndefined, compact, uniq, isEmpty, head } from 'lodash-es'
import { camelCase } from 'change-case'

import API_CONSTANTS from '@/js/helper/api-constants.js'
import BIKE_ATTRIBUTES from '@/js/helper/bike-attributes.js'

import { getContentBaseurl } from '@/js/helper/meta-helper.js'
import { getDataArray } from '@/js/helper/relationships-helper.js'
import {
  getSelfLink,
  getLinkUrl,
  getLinkAllowed,
  hasAllowed,
  getLinkUrlOfType,
  getLinkAllowedOfType,
} from '@/js/helper/links-helper.js'

import { includedCustomBasketAttributes } from '@/js/helper/basket-data-helper.js'

const groupedBikeAttributes = [
  'id',
  'quantity',
  'basketStatus',
  'basketStatusIsOk',
  'basketStockLevel',
  'price',
  'costs',
  'rebate',
  'sum',
  'selfUrl',
  'allowedDelete',
  'allowedPatch',
  'productOrderDate',
  'productOrderCw',
  'paymentTerms',
  'deliveryTerms',
  'customBasketAttributes',
]

export const generateGroupedProducts = products => {
  return Object.values(
    products.reduce((groups, bike) => {
      const productUpgradeBatteryValue = bike?.productUpgradeBattery?.value
      const batteryUpgradeChoice = productUpgradeBatteryValue
        ? head(Object.entries(productUpgradeBatteryValue))
        : []
      const bikeId = [bike.productId, ...batteryUpgradeChoice].join('|')
      console.log(
        '🚀 ~ products.reduce ~ productUpgradeBatteryValue:',
        productUpgradeBatteryValue,
        batteryUpgradeChoice,
        bikeId,
      )
      if (!has(groups, bikeId)) {
        groups[bikeId] = {
          variants: {
            productOrderDate: [],
            productOrderCw: [],
          },
          totalSum: 0,
          totalQuantity: 0,
          relatedIds: [],
        }
      }

      groups[bikeId].totalSum = groups[bikeId].totalSum + bike.sum
      groups[bikeId].totalQuantity = groups[bikeId].totalQuantity + bike.quantity

      if (!has(groups[bikeId], 'id')) {
        groups[bikeId] = {
          ...groups[bikeId],
          ...pick(bike, [
            'name',
            'productId',
            'prodcode',
            'supplierid',
            'mediaUrl',
            'selfUrl',
            'allowedDelete',
            'allowedPatch',
            'price',
            'costs',
            'rebate',
            'sum',
            'currentPaymentTargetName',
            'currentDeliveryTermName',
            'articleType',
            'bidexSegment',
            'brand',
            'currency',
            'deliveryTerms',
            'paymentTerms',
          ]),
          quantity: 0,
          batteryUpgradeChoice,
        }
      }

      if (bike.productOrderCw) {
        groups[bikeId].variants.productOrderCw.push(pick(bike, groupedBikeAttributes))
        groups[bikeId].relatedIds.push(bike.id)
      } else if (bike.productOrderDate) {
        groups[bikeId].variants.productOrderDate.push(pick(bike, groupedBikeAttributes))
        groups[bikeId].relatedIds.push(bike.id)
      } else {
        groups[bikeId] = {
          ...groups[bikeId],
          ...pick(bike, groupedBikeAttributes),
        }
        groups[bikeId].relatedIds.push(bike.id)
      }
      return groups
    }, {}),
  ).map(product => ({
    ...product,
    variants: {
      productOrderDate: sortBy(product.variants.productOrderDate, o => o.productOrderDate.value),
      productOrderCw: sortBy(product.variants.productOrderCw, o => o.productOrderCw.value),
    },
  }))
}

export const generateOrderData = ({ apiData }) => {
  const meta = get(apiData, API_CONSTANTS.META.TYPE, {})
  const links = get(apiData, API_CONSTANTS.LINKS, {})
  const contentBaseurl = getContentBaseurl(meta)
  const includedArray = getDataArray(get(apiData, API_CONSTANTS.INCLUDED, []))

  const price =
    parseFloat(get(apiData, API_CONSTANTS.AIMEOS.BASKET.PATHS.ORDER_BASE_PRICE, 0)) +
    parseFloat(get(apiData, API_CONSTANTS.AIMEOS.BASKET.PATHS.ORDER_BASE_COSTS, 0))

  const currency = get(apiData, API_CONSTANTS.AIMEOS.BASKET.PATHS.ORDER_BASE_CURRENCYID, 'EUR')

  const basketBaseId = get(apiData, API_CONSTANTS.AIMEOS.BASKET.PATHS.ORDER_BASE_ID)

  const basketReference = get(apiData, API_CONSTANTS.AIMEOS.BASKET.PATHS.ORDER_BASE_CUSTOMERREF, '')

  const basketComment = get(apiData, API_CONSTANTS.AIMEOS.BASKET.PATHS.ORDER_BASE_COMMENT, '')

  let basketClientData = {}
  try {
    basketClientData = JSON.parse(
      get(apiData, API_CONSTANTS.AIMEOS.BASKET.PATHS.ORDER_BASE_COMMENT, ''),
    )
  } catch (e) {
    basketClientData = {}
  }

  const products = generateBasketProductData({ includedArray, contentBaseurl, currency })

  const count = products.reduce((count, item) => count + item.quantity, 0)

  const clientProducts = products.reduce((acc, product) => {
    if (!has(acc, product.orderBrand)) {
      acc[product.orderBrand] = { basketBikes: [], basketParts: [], clientSubTotal: 0 }
    }

    acc[product.orderBrand].clientSubTotal = acc[product.orderBrand].clientSubTotal + product.sum

    if (product.articleType === API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ARTICLE_TYPES.BIKE) {
      acc[product.orderBrand].basketBikes.push(product)
    } else {
      acc[product.orderBrand].basketParts.push(product)
    }
    return acc
  }, {})

  // const basketBikes = products.filter(product =>
  //   API_CONSTANTS.AIMEOS.BASKET_PRODUCT.SUPPLIER_CODE_REGEX.BIKE.test(product.supplierid),
  // )

  const clientProductsGrouped = Object.entries(clientProducts).reduce(
    (acc, [client, clientData]) => {
      const uniqueValues = uniq(
        compact(
          [...clientData.basketBikes, ...clientData.basketParts]
            .filter(item => (item?.deliveryTerms || []).length > 1)
            .map(item => get(item, 'customBasketAttributes.productOrderDelivery.value')),
        ),
      )
      const deliveryTerms = get(
        [...clientData.basketBikes, ...clientData.basketParts].find(
          item => !isEmpty(item.deliveryTerms),
        ) || {},
        'deliveryTerms',
        [],
      )
      acc[client] = {
        basketBikesGrouped: generateGroupedProducts(clientData.basketBikes),
        basketSparepartsGrouped: generateGroupedProducts(clientData.basketParts),
        deliveryTerms,
        deliveryTermValue: uniqueValues.length === 1 ? head(uniqueValues) : undefined,
        subTotal: clientData.clientSubTotal,
      }
      return acc
    },
    {},
  )

  // const basketBikesGrouped = generateGroupedProducts(basketBikes)

  // const basketSpareparts = products.filter(product =>
  //   API_CONSTANTS.AIMEOS.BASKET_PRODUCT.SUPPLIER_CODE_REGEX.SPAREPART.test(product.supplierid),
  // )

  // const basketSparepartsGrouped = generateGroupedProducts(basketSpareparts)

  // const basketBikesPrice = basketBikes.reduce((price, product) => price + product.sum, 0)
  // const basketSparepartsPrice = basketSpareparts.reduce((price, product) => price + product.sum, 0)

  // LINKS

  const allowedOrder = getLinkAllowedOfType(links, API_CONSTANTS.LINK.ORDER)
  const allowedSelf = getLinkAllowedOfType(links, API_CONSTANTS.LINK.SELF)
  const allowedBasketProduct = getLinkAllowedOfType(links, API_CONSTANTS.AIMEOS.TYPE.BASKET_PRODUCT)
  const allowedBasketAddress = getLinkAllowedOfType(links, API_CONSTANTS.AIMEOS.TYPE.BASKET_ADDRESS)
  const allowedBasketService = getLinkAllowedOfType(links, API_CONSTANTS.AIMEOS.TYPE.BASKET_SERVICE)

  const orderPostUrl =
    Array.isArray(allowedOrder) && allowedOrder.includes('POST')
      ? getLinkUrlOfType(links, API_CONSTANTS.LINK.ORDER)
      : undefined

  const selfPostUrl =
    Array.isArray(allowedSelf) && allowedSelf.includes('POST')
      ? getLinkUrlOfType(links, API_CONSTANTS.LINK.SELF)
      : undefined

  const selfPatchUrl =
    Array.isArray(allowedSelf) && allowedSelf.includes('PATCH')
      ? getLinkUrlOfType(links, API_CONSTANTS.LINK.SELF)
      : undefined

  const basketProductPostUrl =
    Array.isArray(allowedBasketProduct) && allowedBasketProduct.includes('POST')
      ? getLinkUrlOfType(links, API_CONSTANTS.AIMEOS.TYPE.BASKET_PRODUCT)
      : undefined

  const basketAddressPostUrl =
    Array.isArray(allowedBasketAddress) && allowedBasketAddress.includes('POST')
      ? getLinkUrlOfType(links, API_CONSTANTS.AIMEOS.TYPE.BASKET_ADDRESS)
      : undefined

  const basketServicePostUrl =
    Array.isArray(allowedBasketService) && allowedBasketService.includes('POST')
      ? getLinkUrlOfType(links, API_CONSTANTS.AIMEOS.TYPE.BASKET_SERVICE)
      : undefined

  // ADDRESSES + SERVICES

  const basketAddressIncluded = includedArray.filter(
    item => get(item, API_CONSTANTS.TYPE) === API_CONSTANTS.AIMEOS.TYPE.BASKET_ADDRESS,
  )
  const basketServiceIncluded = includedArray.filter(
    item => get(item, API_CONSTANTS.TYPE) === API_CONSTANTS.AIMEOS.TYPE.BASKET_SERVICE,
  )

  const basketPaymentAddressIncluded = basketAddressIncluded.filter(
    item => get(item, API_CONSTANTS.ID) === API_CONSTANTS.AIMEOS.BASKET.CONSTANTS.ID.PAYMENT,
  )

  const basketDeliveryAddressIncluded = basketAddressIncluded.filter(
    item => get(item, API_CONSTANTS.ID) === API_CONSTANTS.AIMEOS.BASKET.CONSTANTS.ID.DELIVERY,
  )

  const basketPaymentServiceIncluded = basketServiceIncluded.filter(
    item => get(item, API_CONSTANTS.ID) === API_CONSTANTS.AIMEOS.BASKET.CONSTANTS.ID.PAYMENT,
  )

  const basketDeliveryServiceIncluded = basketServiceIncluded.filter(
    item => get(item, API_CONSTANTS.ID) === API_CONSTANTS.AIMEOS.BASKET.CONSTANTS.ID.DELIVERY,
  )

  return {
    contentBaseurl,
    count,
    price,
    currency,
    basketBaseId,
    basketClientData,
    basketReference,
    basketComment,
    products,
    clientProductsGrouped,
    // basketBikes,
    // basketBikesGrouped,
    // basketSpareparts,
    // basketSparepartsGrouped,
    // basketBikesPrice,
    // basketSparepartsPrice,
    orderPostUrl,
    selfPostUrl,
    selfPatchUrl,
    basketProductPostUrl,
    basketAddressPostUrl,
    basketServicePostUrl,
    basketPaymentAddressIncluded,
    basketDeliveryAddressIncluded,
    basketPaymentServiceIncluded,
    basketDeliveryServiceIncluded,
    included: includedArray,
  }
}

const getBasketProductsFromIncluded = ({ includedArray }) =>
  includedArray.filter(
    item =>
      get(item, API_CONSTANTS.TYPE) === API_CONSTANTS.AIMEOS.BASKET.CONSTANTS.TYPE.BASKET_PRODUCT,
  )

const generateBasketProductData = ({ includedArray, contentBaseurl, currency }) => {
  return getBasketProductsFromIncluded({ includedArray }).map(
    ({ id, attributes = {}, links = {} }) => {
      const name = get(attributes, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.NAME, '')
      const productId = get(attributes, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.PRODUCTID, '')
      const prodcode = get(
        attributes,
        API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.PRODUCTCODE,
        '',
      )
      const supplierid = get(attributes, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.SUPPLIERID)
      const mediaUrl = `${contentBaseurl}/${get(
        attributes,
        API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.MEDIAURL,
      )}`
      const quantity = parseInt(
        get(attributes, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.QUANTITY),
        10,
      )
      const basketStatus = get(attributes, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.DP_STATUS)
      const basketStatusIsOk = isUndefined(basketStatus) || basketStatus === 'ok'
      const basketStockLevel = get(
        attributes,
        API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.DP_STOCK_LEVEL,
      )
      const price = parseFloat(
        get(attributes, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.PRICE, 0),
      )
      const costs = parseFloat(
        get(attributes, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.COSTS, 0),
      )
      const rebate = parseFloat(
        get(attributes, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.REBATE, 0),
      )
      const sum = quantity * (price + costs) - rebate
      const paymentTerms = get(
        attributes,
        API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.PAYMENT_TERMS,
        [],
      )
      const deliveryTerms = get(
        attributes,
        API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.DELIVERY_TERMS,
        [],
      )
      const articleType = API_CONSTANTS.AIMEOS.BASKET_PRODUCT.SUPPLIER_CODE_REGEX.BIKE.test(
        supplierid,
      )
        ? API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ARTICLE_TYPES.BIKE
        : API_CONSTANTS.AIMEOS.BASKET_PRODUCT.SUPPLIER_CODE_REGEX.ACCESSORY.test(supplierid)
        ? API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ARTICLE_TYPES.ACCESSORY
        : API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ARTICLE_TYPES.SPAREPART

      const bidexSegment = get(
        attributes,
        API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.BIDEX_SEGMENT,
      )
      const brand = get(attributes, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.BRAND)

      const selfUrl = getLinkUrl(getSelfLink(links))
      const selfAllowed = getLinkAllowed(getSelfLink(links))
      const hasSelfAllowed = hasAllowed(selfAllowed)
      const allowedDelete =
        !hasSelfAllowed || !!selfAllowed.find(perm => perm === API_CONSTANTS.ALLOW.DELETE)
      const allowedPatch =
        !hasSelfAllowed || !!selfAllowed.find(perm => perm === API_CONSTANTS.ALLOW.PATCH)

      const additionalAttributes = get(
        attributes,
        API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.ATTRIBUTE,
        [],
      )

      const customAttributes = additionalAttributes.reduce((acc, attr) => {
        const code = get(attr, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.ATTRIBUTES.CODE)
        if (BIKE_ATTRIBUTES.GROUP.BASKET.includes(code)) {
          acc[camelCase(code)] = {
            code,
            name: get(attr, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.ATTRIBUTES.NAME),
            value: get(attr, API_CONSTANTS.AIMEOS.BASKET_PRODUCT.ATTRIBUTE.ATTRIBUTES.VALUE),
          }
        }
        return acc
      }, Object.fromEntries(BIKE_ATTRIBUTES.GROUP.BASKET.map(i => [camelCase(i), undefined])))

      const currentDeliveryTermName = customAttributes?.productOrderDelivery?.value
      const currentPaymentTargetName = paymentTerms?.find(
        term => term.code === customAttributes?.productOrderPaymentTarget?.value,
      )?.text

      const orderBrand = customAttributes?.productOrderBrand?.value

      return {
        id,
        name,
        productId,
        prodcode,
        orderBrand,
        supplierid,
        mediaUrl,
        quantity,
        basketStatus,
        basketStatusIsOk,
        basketStockLevel,
        price,
        costs,
        rebate,
        sum,
        selfUrl,
        allowedDelete,
        allowedPatch,
        paymentTerms,
        deliveryTerms,
        currentDeliveryTermName,
        currentPaymentTargetName,
        articleType,
        bidexSegment,
        brand,
        currency,
        customBasketAttributes: pick(customAttributes, includedCustomBasketAttributes),
        ...pick(customAttributes, ['productOrderDate', 'productOrderCw', 'productUpgradeBattery']),
      }
    },
  )
}
